import React from 'react';

import type { Event } from '@vlabs/api/luna-api/model';
import { apiContainer } from '@vlabs/api-bindings';
import { AttentionCircleFilledIcon, DetectionIcon } from '@vlabs/icons';
import { MultiSample } from '@vlabs/shared/components/sample/MultiSample';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { EventStreamLink } from '@vlabs/shared/components/stream-link/EventStreamLink';
import { formatDateTime } from '@vlabs/shared/utils/date';
import { Tag, Control, useWindowSize } from '@vlabs/uikit';
import { routes } from '@vlapps/point/src/app/routes';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';

import { BaseEventPreview } from './BaseEventPreview';
import st from './EventPreviewFaceBody.module.sass';
import { selectNotifyEvents } from '../../selectors';
import { useEventInfoSettingsLocation } from '../../useEventInfoSettingsSearchParams';
import { useMatchData } from '../../useMatchData';
import { useSelectedWorkspaceEvent } from '../../useSelectedWorkspaceEvent';

interface EventPreviewFaceBodyProps {
  event: Event;
  onClick: (params: { event: Event }) => void;
}

export function EventPreviewFaceBody({ event, onClick }: EventPreviewFaceBodyProps): React.JSX.Element {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const notifyEvents = useSelector(selectNotifyEvents);
  const { eventInfoSettings } = useEventInfoSettingsLocation();
  const { selectedEventId } = useSelectedWorkspaceEvent();

  const [matchData, matchType] = useMatchData(event?.top_match);

  const location = buildLocationString(event);

  const isActive = selectedEventId === event?.event_id;
  const isAlarm = event?.tags?.includes('alarm');
  const isAlarmEventUnviewed = isAlarm && notifyEvents.some((e) => e.event_id === event?.event_id);

  const bodySampleId = event?.body_detections?.find(({ sample_id }) => sample_id)?.sample_id;
  // FIXME: apiContainer
  const bodySampleUrl = apiContainer.lunaClient.samples.getURL('bodies', bodySampleId);
  const faceSampleId = event?.face_detections?.find(({ sample_id }) => sample_id)?.sample_id;
  const faceSampleUrl = apiContainer.lunaClient.samples.getURL('faces', faceSampleId);

  const renderLists = () => {
    if (!matchData?.lists?.length) {
      return <span>{t('lunapoint:person_card.no_lists')}</span>;
    }

    return (
      <React.Fragment>
        <span>{t('lunapoint:person_card.lists')}</span>
        <div className={st.ListsList}>
          {matchData?.lists?.map((list) => (
            <Control.Link
              className={st.ListLink}
              key={list?.list_id}
              kind="secondary"
              to={generatePath(routes.lists.read, { listId: list?.list_id })}
            >
              {list?.user_data ?? list?.list_id}
            </Control.Link>
          ))}
        </div>
      </React.Fragment>
    );
  };

  return (
    <BaseEventPreview
      data-testid={event?.event_id}
      isActive={isActive}
      isAlarm={isAlarmEventUnviewed}
      onClick={() => onClick({ event })}
    >
      <BaseEventPreview.Main>
        <BaseEventPreview.Media>
          <div className={st.Samples}>
            <MultiSample
              className={st.MultiSample}
              data-testid="eventInformationCell.sample"
              mainSample={eventInfoSettings?.mainSample}
              primary={{
                imageUrl: faceSampleUrl,
                type: 'face',
              }}
              sampleType={eventInfoSettings?.sampleType}
              secondary={{
                imageUrl: bodySampleUrl,
                type: 'body',
              }}
            />

            {!!matchData && (
              <MultiSample
                className={st.MultiSample}
                data-testid="eventInformationCell.matchSample"
                mainSample={eventInfoSettings?.mainSample}
                primary={{
                  imageUrl:
                    matchType === 'face'
                      ? matchData.uiAvatarURL
                      : matchData.uiFaceDetection?.uiSampleURL,
                  type: 'face',
                }}
                sampleType={eventInfoSettings?.sampleType}
                secondary={{
                  imageUrl: matchData.uiBodyDetection?.uiSampleURL,
                  type: 'body',
                }}
              />
            )}
          </div>
        </BaseEventPreview.Media>

        <BaseEventPreview.Info>
          <div className={st.SamplesData}>
            <div className={st.Tags}>
              <Tag
                className={isAlarm ? st.Tag_alarm : ''}
                data-testid="eventInformationCell.tagType"
                icon={
                  isAlarm ? (
                    <AttentionCircleFilledIcon className={st.PulsingIcon} />
                  ) : (
                    ''
                  )
                }
                kind="neutral"
                size={windowSize.width < 1920 ? 'medium' : 'large'}
                value={t(
                  `lunapoint:event_card.${event?.top_match ? 'match' : 'detection'}`,
                )}
              />

              {!!event?.top_match && (
                <SimilarityTag
                  data-testid="eventInformationCell.similarityTagType"
                  icon={<DetectionIcon />}
                  size={windowSize.width < 1920 ? 'medium' : 'large'}
                  title={t(
                    `lunapoint:person_card.${matchType === 'face' ? 'face' : 'event'}`,
                  )}
                  value={event?.top_match?.similarity}
                />
              )}
            </div>

            {!!eventInfoSettings.isVisibleDate && (
              <div
                className={st.InfoText}
                data-testid="eventInformationCell.createTime"
              >
                {formatDateTime(event?.create_time)}
              </div>
            )}

            {!!eventInfoSettings.isVisibleSource && (
              <div
                className={cn(st.InfoText, st.InfoText_empty)}
                data-testid="eventInformationCell.sources"
              >
                {!!event?.stream_id && (
                  <EventStreamLink
                    className={st.StreamLink}
                    hasTruncatedLink
                    hideId
                    key={!!matchData}
                    kind="secondary"
                    placeholders={{
                      removed: t(
                        'lunapoint:event_card.videostream.removed',
                      ),
                      unavailable: t(
                        'lunapoint:event_card.videostream.unavailable',
                      ),
                    }}
                    size="medium"
                    streamId={event.stream_id}
                  />
                )}
                {!event?.stream_id && !!event?.source && (
                  <div
                    className={cn(st.InfoText, {
                      [st.InfoText_empty]:
                        location === t('lunapoint:no_address'),
                    })}
                  >
                    {event.source}
                  </div>
                )}
              </div>
            )}
          </div>
        </BaseEventPreview.Info>
      </BaseEventPreview.Main>

      {!!(eventInfoSettings.isVisibleAddress || eventInfoSettings.isVisibleLists) && (
        <BaseEventPreview.Footer>
          {!!eventInfoSettings.isVisibleAddress && (
            <div
              className={cn(st.InfoText, {
                [st.InfoText_empty]:
                  location === t('lunapoint:no_address'),
              })}
              data-testid="eventInformationCell.location"
            >
              {location}
            </div>
          )}

          {!!eventInfoSettings.isVisibleLists && (
            <div
              className={st.Lists}
              data-testid="eventInformationCell.lists"
            >
              {renderLists()}
            </div>
          )}
        </BaseEventPreview.Footer>
      )}
    </BaseEventPreview>
  );
}
